import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Subject, takeUntil } from 'rxjs';
import { AppStateService } from 'src/app/services/app-state.service';
import { LabpartnerService } from 'src/app/services/labpartner.service';
import {
  Experiment,
  ExperimentStatus,
  ICExperiment,
  ICInstrumentOrder,
  ICExperimentStatus,
  ICLegendData,
  ICLegendInstruction,
} from 'src/app/services/labpartner.service.model';
import {
  EVENT_IC_SEQUENCE_SAVED,
  EVENT_IC_SET_EXPERIMENT_READY,
} from 'src/app/services/logging-constants';
import { LoggingService } from 'src/app/services/logging.service';
import { UserAccountService } from 'src/app/services/user-account.service';
import { DialogService } from 'src/app/shared/dialog.service';
import { NotificationService } from 'src/app/shared/notification.service';
import { BaseComponent } from 'src/app/support/base.component';
import { ICRoundService } from '../ic-round.service';
@Component({
  selector: 'ic-sequence',
  templateUrl: './ic-sequence.component.html',
  styleUrls: ['./ic-sequence.component.scss'],
  standalone: false,
})
export class ICSequenceComponent extends BaseComponent implements OnChanges {
  @Output()
  onChangeSetReady = new EventEmitter<void>();
  @Output()
  onChangeSequenceSaved = new EventEmitter<void>();
  @Input()
  experiment: ICExperiment | null = null;
  @Output()
  onLoadPreviousSequence = new EventEmitter<void>();

  @Input()
  legendData?: ICLegendData[];

  @Input()
  legendInstructions?: ICLegendInstruction[];

  dataSource: MatTableDataSource<ICLegendData> = new MatTableDataSource<ICLegendData>();

  sequenceDataSource = new MatTableDataSource<ICLegendInstruction>([]);

  displayedColumns: string[] = ['legendDataId', 'name', 'addToSequence', 'n'];
  sequenceDisplayedColumns: string[] = [
    'sequenceOrder',
    'legendDataId',
    'name',
    'incubationS',
    'delayS',
    'numberOfImageSets',
    'order',
  ];

  currentRoles: string[] = [];

  private readonly _destroying$ = new Subject<void>();

  constructor(
    public apiService: LabpartnerService,
    private dialogService: DialogService,
    private notificationService: NotificationService,
    private appState: AppStateService,
    private loggingService: LoggingService,
    private accountService: UserAccountService,
    public icRoundService: ICRoundService,
  ) {
    super();
  }

  protected ngOnDestroyInternal(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.legendData) {
      this.dataSource.data = changes.legendData.currentValue;
      this.dataSource._updateChangeSubscription();
    }
    if (changes.legendInstructions) {
      this.sequenceDataSource.data = changes.legendInstructions.currentValue;
      this.sequenceDataSource._updateChangeSubscription();
    }
  }

  ngOnInit(): void {
    this.accountService.currentRoles$.pipe(takeUntil(this._destroying$)).subscribe(roles => {
      this.currentRoles = roles;
    });
  }

  // Add to sequence button click handler
  addToSequence(element: any): void {
    this.sequenceDataSource.data.push({
      legendDataId: element.legendDataId,
      incubationS: this.experiment?.incubation ? this.experiment?.incubation : 0,
      delayS: this.experiment?.delay ? this.experiment?.delay : 0,
      numberOfImageSets: this.experiment?.numberOfImages ? this.experiment?.numberOfImages : 0,
      roundNumber: this.icRoundService.getCurrentRound(),
    });
    this.sequenceDataSource._updateChangeSubscription();
  }

  // Remove from sequence button click handler
  moveUp(index: number): void {
    if (index > 0) {
      const data = this.sequenceDataSource.data;
      const temp = data[index];
      data[index] = data[index - 1];
      data[index - 1] = temp;
      this.sequenceDataSource.data = [...data];
    }
  }

  // Move down button click handler
  moveDown(index: number): void {
    if (index < this.sequenceDataSource.data.length - 1) {
      const data = this.sequenceDataSource.data;
      const temp = data[index];
      data[index] = data[index + 1];
      data[index + 1] = temp;
      this.sequenceDataSource.data = [...data];
    }
  }

  // Remove from sequence button click handler
  removeRow(index: number): void {
    const data = this.sequenceDataSource.data;
    data.splice(index, 1);
    this.sequenceDataSource.data = [...data];
  }

  // Clear all button click handler
  drop(event: CdkDragDrop<any[]>): void {
    const previousIndex = event.previousIndex;
    const currentIndex = event.currentIndex;
    const data = this.sequenceDataSource.data;
    const element = data.splice(previousIndex, 1)[0];
    data.splice(currentIndex, 0, element);
    this.sequenceDataSource.data = [...data];
  }

  // Add all button click handler
  // This function adds all legends from the dataSource to the sequenceDataSource if they are not already present
  addAll() {
    const existinglegendDataIds = this.sequenceDataSource.data.map(item => item.legendDataId);
    const legendsToAdd = this.dataSource.data.filter(
      legend => !existinglegendDataIds.includes(legend.legendDataId),
    );
    legendsToAdd.forEach(legend => {
      this.sequenceDataSource.data.push({
        legendDataId: legend.legendDataId,
        incubationS: this.experiment?.incubation ? this.experiment?.incubation : 0,
        delayS: this.experiment?.delay ? this.experiment?.delay : 0,
        numberOfImageSets: this.experiment?.numberOfImages ? this.experiment?.numberOfImages : 0,
        roundNumber: this.icRoundService.getCurrentRound(),
      });
    });
    this.sequenceDataSource._updateChangeSubscription();
  }

  // Get the number of times a legend has been added to the sequence
  getTimesAdded(element: any): string {
    const timesAdded = this.sequenceDataSource.data.filter(
      item => item.legendDataId === element.legendDataId,
    ).length;
    return timesAdded > 0 ? timesAdded + '' : '';
  }

  getConfiguredInstrumentCount(): number {
    return this.getCurrentRoundInstrumentOrders().length;
  }

  getLegendDistributionMessage(): string {
    const instrumentCount = this.getConfiguredInstrumentCount();
    const instrumentLabel = instrumentCount === 1 ? 'instrument' : 'instruments';

    return `Each legendId will be send to all the ${instrumentCount} ${instrumentLabel} previously set`;
  }

  // Check if the add all button should be disabled
  isAddAllDisabled(): boolean {
    return (
      this.isDisabled() ||
      this.dataSource.data.every(legend =>
        this.sequenceDataSource.data.some(
          sequence => sequence.legendDataId === legend.legendDataId,
        ),
      )
    );
  }

  // Check general disabled state of the component
  isDisabled() {
    return !this.experiment || this.experiment.status !== ExperimentStatus.Created;
  }

  isSetReadyDisabled(): boolean {
    return (
      this.isDisabled() ||
      this.sequenceDataSource.data.length === 0 ||
      this.sequenceDataSource.data.some(
        sequence =>
          sequence.incubationS < 0 ||
          (sequence.incubationS > 0 && sequence.incubationS < 10) ||
          sequence.delayS < 0 ||
          sequence.numberOfImageSets < 0,
      )
    );
  }

  async setReady() {
    if (!this.experiment?.icExperimentId) {
      return;
    }

    this.subscription.add(
      this.apiService
        .checkICExperimentInstrumentsAvailability(this.experiment?.icExperimentId)
        .subscribe(async response => {
          if (!this.experiment?.icExperimentId) {
            return;
          }

          let dialogRef;
          // Check if any instruments are in use in another experiment
          if (response && response.length > 0) {
            let message = response
              .map(
                (r: any) =>
                  `The Instrument ${r.InstrumentSerial} is used in LP exp ${r.ExperimentId}`,
              )
              .join('\n');
            message += `\n\nThe experiment(s) should be set to 'Analyzing Images' to free up the instruments and allow this experiment to be set as 'Ready'. If that is not possible, go back to Inputs, remove or change the conflicting instrument, save the experiment, and try again.`;
            dialogRef = this.dialogService.openConfirmDialog(message, {
              isOk: true,
              title: 'Instruments availability',
              isV2: true,
              width: '500px',
            });

            await dialogRef.afterClosed().toPromise();
            return; // Cancel the operation if the dialog is closed
          }

          // If no instruments are in use, confirm the action
          // Show confirmation dialog before setting the experiment as ready
          let message = `Are you sure you want to set this experiment as Ready?`;
          message += `\n\nNo Changes can be made after the experiment is set as ready.`;
          dialogRef = this.dialogService.openConfirmDialog(message, {
            isOk: false,
            title: 'Confirmation',
            isV2: true,
            width: '500px',
          });

          const dialogRes = await dialogRef.afterClosed().toPromise();
          if (!dialogRes) {
            return; // Cancel the opertaion if the response is NO
          }

          // If no instruments are in use, proceed to set the experiment as ready
          this.sequenceDataSource.data.forEach((item, index) => {
            item.instructionOrder = index + 1;
          });
          this.subscription.add(
            this.apiService
              .setICExperimentReady(
                this.experiment?.icExperimentId,
                this.sequenceDataSource.data,
                this.icRoundService.getCurrentRound(),
              )
              .subscribe(
                () => {
                  if (!this.experiment?.experimentId) {
                    return;
                  }
                  this.onChangeSetReady.emit();
                  this.experiment.status = ICExperimentStatus.Ready;
                  const props: { [key: string]: number } = {
                    ExperimentId: this.experiment?.experimentId,
                    ICExperimentId: this.experiment?.icExperimentId
                      ? this.experiment?.icExperimentId
                      : 0,
                    SequenceCount: this.sequenceDataSource.data.length,
                    ExperimentOwner: this.experiment?.createdBy ?? 0,
                  };
                  this.loggingService.logEvent(EVENT_IC_SET_EXPERIMENT_READY, props);

                  this.notificationService.success('Experiment set as Ready.');
                  this.subscription.add(
                    this.apiService
                      .getICExperiment(this.experiment?.experimentId)
                      .subscribe(experiment => {
                        this.appState.SetCurrentExperiment(experiment as Experiment);
                      }),
                  );
                },
                () => {
                  this.notificationService.error('Error setting experiment as Ready.');
                },
              ),
          );
        }),
    );
  }

  async saveSequence() {
    if (!this.experiment?.icExperimentId) {
      return;
    }

    // If no instruments are in use, proceed to set the experiment as ready
    this.sequenceDataSource.data.forEach((item, index) => {
      item.instructionOrder = index + 1;
    });
    this.subscription.add(
      this.apiService
        .saveSequence(
          this.experiment?.icExperimentId,
          this.sequenceDataSource.data,
          this.icRoundService.getCurrentRound(),
        )
        .subscribe(
          () => {
            if (!this.experiment?.experimentId) {
              return;
            }
            this.onChangeSequenceSaved.emit();
            const props: { [key: string]: number } = {
              ExperimentId: this.experiment?.experimentId,
              ICExperimentId: this.experiment?.icExperimentId ? this.experiment?.icExperimentId : 0,
              SequenceCount: this.sequenceDataSource.data.length,
              ExperimentOwner: this.experiment?.createdBy ?? 0,
            };
            this.loggingService.logEvent(EVENT_IC_SEQUENCE_SAVED, props);

            this.notificationService.success('Experiment sequence saved successfully.');
          },
          () => {
            this.notificationService.error('Error saving experiment sequence.');
          },
        ),
    );
  }

  // Get the name of a legend based on its ID
  getLegendName(legendDataId: number): string {
    const legend = this.dataSource.data.find(item => item.legendDataId === legendDataId);
    return legend ? legend.name : '';
  }

  // Get the ID of a legend based on its ID
  getLegendId(legendDataId: number): any {
    const legend = this.dataSource.data.find(item => item.legendDataId === legendDataId);
    return legend ? legend.legendId : 0;
  }

  parseExperiment(): Experiment {
    return this.experiment as Experiment;
  }

  removeAll(): void {
    this.sequenceDataSource.data = [];
    this.sequenceDataSource._updateChangeSubscription();
  }

  loadPreviousSequence(): void {
    this.onLoadPreviousSequence.emit();
  }

  private getCurrentRoundInstrumentOrders(): ICInstrumentOrder[] {
    return (this.experiment?.instrumentOrders || []).filter(
      order => Number(order.roundNumber ?? 1) === Number(this.icRoundService.getCurrentRound()),
    );
  }
}
